import React, { useEffect, useRef } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { scrollToListTop } from "../../../utils/scrollToListTop";
import CustomSelect from "../CustomSelect/CustomSelect";
import styles from "./CustomPagination.module.css";

/**
 * CustomPagination component props
 */
interface CustomPaginationProps {
  /** Current active page (1-indexed) */
  current: number;
  /** Total number of items */
  total: number;
  /** Number of items per page */
  pageSize?: number;
  /** Available options for rows per page */
  pageSizeOptions?: number[];
  /** Callback when page size changes */
  onPageSizeChange?: (pageSize: number) => void;
  /** Callback when page changes */
  onChange: (page: number) => void;
  /** Additional CSS class */
  className?: string;
}

/**
 * The list this pager controls: the nearest visible block rendered before it
 * (separators such as antd's Divider are skipped).
 */
const findListBefore = (pager: HTMLElement): Element | null => {
  for (let node: Element | null = pager; node; node = node.parentElement) {
    for (
      let prev = node.previousElementSibling;
      prev;
      prev = prev.previousElementSibling
    ) {
      if (
        prev.getAttribute("role") !== "separator" &&
        prev.getBoundingClientRect().height > 0
      ) {
        return prev;
      }
    }
  }
  return null;
};

/**
 * Arrow Left Icon
 */
const ArrowLeftIcon: React.FC = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M15.8332 10H4.1665M4.1665 10L9.99984 15.8333M4.1665 10L9.99984 4.16667"
      stroke="currentColor"
      strokeWidth="1.67"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Arrow Right Icon
 */
const ArrowRightIcon: React.FC = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M4.1665 10H15.8332M15.8332 10L9.99984 4.16667M15.8332 10L9.99984 15.8333"
      stroke="currentColor"
      strokeWidth="1.67"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Generate pagination items with smart ellipsis
 */
const getPaginationItems = (
  current: number,
  total: number,
): (number | string)[] => {
  const items: (number | string)[] = [];
  const showEllipsis = total > 7;

  if (!showEllipsis) {
    // Show all pages if total <= 7
    for (let i = 1; i <= total; i++) {
      items.push(i);
    }
    return items;
  }

  // Always show first page
  items.push(1);

  if (current <= 3) {
    // Near the start: 1 2 3 ... 9 10
    items.push(2, 3);
    items.push("...");
    items.push(total - 1, total);
  } else if (current >= total - 2) {
    // Near the end: 1 2 ... 8 9 10
    items.push("...");
    items.push(total - 2, total - 1, total);
  } else {
    // In the middle: 1 ... 4 5 6 ... 10
    items.push("...");
    items.push(current - 1, current, current + 1);
    items.push("...");
    items.push(total);
  }

  return items;
};

/**
 * CustomPagination component
 * A reusable pagination component following Akinda design system
 */
export const CustomPagination: React.FC<CustomPaginationProps> = ({
  current,
  total,
  pageSize,
  pageSizeOptions,
  onPageSizeChange,
  onChange,
  className,
}) => {
  const { t } = useTranslation();
  const wrapperRef = useRef<HTMLDivElement>(null);

  const effectivePageSize = pageSize || 10;
  const totalPages = Math.ceil(total / effectivePageSize);
  const options = pageSizeOptions ?? [10, 20, 50];

  // Start the new page from its first row. Measured before React re-renders,
  // so the list's top is still where the user last saw it.
  const scrollListIntoView = () => {
    if (wrapperRef.current) scrollToListTop(findListBefore(wrapperRef.current));
  };

  // A page past the end (last row deleted, old link) would show an empty list
  // with a dead "Previous" button: move to the last page instead.
  useEffect(() => {
    if (totalPages > 0 && current > totalPages) onChange(totalPages);
  }, [current, totalPages, onChange]);

  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages || page === current) {
      return;
    }
    onChange(page);
    scrollListIntoView();
  };

  const paginationItems = getPaginationItems(current, totalPages);

  const canGoPrev = current > 1;
  const canGoNext = current < totalPages;

  return (
    <div ref={wrapperRef} className={classNames(styles.wrapper, className)}>
      <div>
        {onPageSizeChange && (
          <CustomSelect
            className={styles.selectWrapper}
            value={String(pageSize)}
            onChange={(value) => {
              if (!value) return;
              const nextSize = Number(value);
              if (Number.isNaN(nextSize)) return;
              if (onPageSizeChange) {
                onPageSizeChange(nextSize);
                scrollListIntoView();
              }
            }}
            options={options.map((size) => ({
              label: String(size),
              value: String(size),
            }))}
          />
        )}
      </div>
      <div className={styles.paginationGroup}>
        {/* Previous Button */}
        <button
          className={classNames(styles.prevNextButton, {
            [styles.disabled]: !canGoPrev,
          })}
          onClick={() => handlePageChange(current - 1)}
          disabled={!canGoPrev}
          aria-label={t("pages.common.pagination.previous")}
        >
          <span className={styles.icon}>
            <ArrowLeftIcon />
          </span>
          <span className={styles.prevNextLabel}>
            {t("pages.common.pagination.previous")}
          </span>
        </button>

        {/* Page Numbers */}
        {paginationItems.map((item, index) => {
          if (item === "...") {
            return (
              <div
                key={`ellipsis-${index}`}
                className={styles.button}
                style={{ cursor: "default" }}
              >
                ...
              </div>
            );
          }

          const pageNumber = item as number;
          const isActive = pageNumber === current;

          return (
            <button
              key={pageNumber}
              className={classNames(styles.button, {
                [styles.active]: isActive,
              })}
              onClick={() => handlePageChange(pageNumber)}
              aria-label={`Page ${pageNumber}`}
              aria-current={isActive ? "page" : undefined}
            >
              {pageNumber}
            </button>
          );
        })}

        {/* Next Button */}
        <button
          className={classNames(styles.nextButton, {
            [styles.disabled]: !canGoNext,
          })}
          onClick={() => handlePageChange(current + 1)}
          disabled={!canGoNext}
          aria-label={t("pages.common.pagination.next")}
        >
          <span className={styles.prevNextLabel}>
            {t("pages.common.pagination.next")}
          </span>
          <span className={styles.icon}>
            <ArrowRightIcon />
          </span>
        </button>
      </div>
    </div>
  );
};
